import React, { useState, useEffect, useCallback } from 'react';
import './BeansPicker.css';

// Point this at wherever your API actually lives. Your server.js listens on
// port 3081 over HTTPS with no path prefix beyond /api, so this is the likely
// value -- adjust if you proxy /api through the same origin as the React app.
const API_BASE = 'https://forteindiedev.com:3081/api';

const PROFILES = ['Bass', 'Ink', 'Icey'];
const PROFILE_STORAGE_KEY = 'beansPickerProfile';
const SCORE_VALUES = [0, 1, 2, 3];
const POLL_INTERVAL_MS = 5000;

function BeansPicker() {
    const [profile, setProfile] = useState(() => localStorage.getItem(PROFILE_STORAGE_KEY) || null);
    const [options, setOptions] = useState([]);
    const [newRestaurant, setNewRestaurant] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null); // restaurant id currently being scored, for disabling buttons

    const fetchOptions = useCallback(async ({ silent } = {}) => {
        try {
            if (!silent) setLoading(true);
            const res = await fetch(`${API_BASE}/beans/options`);
            if (!res.ok) throw new Error('Failed to load options');
            const data = await res.json();
            setOptions(data);
            setError(null);
        } catch (err) {
            console.error(err);
            setError("Couldn't reach the server. Is the backend running?");
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOptions();
        const interval = setInterval(() => fetchOptions({ silent: true }), POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchOptions]);

    const handleChooseProfile = (name) => {
        setProfile(name);
        localStorage.setItem(PROFILE_STORAGE_KEY, name);
    };

    const handleSwitchProfile = () => {
        setProfile(null);
        localStorage.removeItem(PROFILE_STORAGE_KEY);
    };

    const handleAddRestaurant = async (e) => {
        e.preventDefault();
        const name = newRestaurant.trim();
        if (!name) return;
        try {
            const res = await fetch(`${API_BASE}/beans/options`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name }),
            });
            if (!res.ok) throw new Error('Failed to add restaurant');
            setNewRestaurant('');
            fetchOptions({ silent: true });
        } catch (err) {
            console.error(err);
            setError("Couldn't add that restaurant.");
        }
    };

    const handleSetScore = async (restaurantId, score) => {
        if (!profile) return;
        setBusyId(restaurantId);

        const existing = options.find((o) => o.id === restaurantId);
        const currentScore = existing?.scores?.[profile];

        try {
            if (currentScore === score) {
                // Clicking the same value again clears your score for this restaurant.
                await fetch(`${API_BASE}/beans/scores/${restaurantId}/${profile}`, { method: 'DELETE' });
            } else {
                await fetch(`${API_BASE}/beans/scores`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ profile_name: profile, restaurant_id: restaurantId, score }),
                });
            }
            await fetchOptions({ silent: true });
        } catch (err) {
            console.error(err);
            setError("Couldn't save that score.");
        } finally {
            setBusyId(null);
        }
    };

    const handleRemoveRestaurant = async (restaurantId) => {
        try {
            await fetch(`${API_BASE}/beans/options/${restaurantId}`, { method: 'DELETE' });
            fetchOptions({ silent: true });
        } catch (err) {
            console.error(err);
            setError("Couldn't remove that restaurant.");
        }
    };

    const handleClearPoll = async () => {
        if (!window.confirm('Clear the whole poll? This wipes every restaurant and every score for everyone.')) {
            return;
        }
        try {
            await fetch(`${API_BASE}/beans/clear`, { method: 'POST' });
            fetchOptions({ silent: true });
        } catch (err) {
            console.error(err);
            setError("Couldn't clear the poll.");
        }
    };

    if (!profile) {
        return (
            <div className="beans-picker">
                <h1>Where should we eat?</h1>
                <p className="beans-picker-subtitle">Pick who you are to get started.</p>
                <div className="beans-picker-profile-select">
                    {PROFILES.map((name) => (
                        <button
                            key={name}
                            className="beans-picker-profile-button"
                            onClick={() => handleChooseProfile(name)}
                        >
                            {name}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    const topScore = options.length > 0 ? options[0].total_score : 0;

    return (
        <div className="beans-picker">
            <div className="beans-picker-header">
                <h1>Where should we eat?</h1>
                <div className="beans-picker-you">
                    You're <strong>{profile}</strong>
                    <button className="beans-picker-link-button" onClick={handleSwitchProfile}>
                        (switch)
                    </button>
                </div>
            </div>

            {error && <div className="beans-picker-error">{error}</div>}

            <form className="beans-picker-add-form" onSubmit={handleAddRestaurant}>
                <input
                    type="text"
                    placeholder="Add a restaurant..."
                    value={newRestaurant}
                    onChange={(e) => setNewRestaurant(e.target.value)}
                    maxLength={100}
                />
                <button type="submit">Add</button>
            </form>

            {loading ? (
                <p className="beans-picker-subtitle">Loading...</p>
            ) : options.length === 0 ? (
                <p className="beans-picker-subtitle">No restaurants yet -- add one above.</p>
            ) : (
                <ul className="beans-picker-list">
                    {options.map((option) => {
                        const myScore = option.scores?.[profile];
                        const isWinning = option.total_score === topScore && topScore > 0;
                        return (
                            <li
                                key={option.id}
                                className={`beans-picker-item${isWinning ? ' beans-picker-item-winning' : ''}`}
                            >
                                <div className="beans-picker-item-top">
                                    <span className="beans-picker-item-name">{option.name}</span>
                                    <span className="beans-picker-item-total">{option.total_score} pts</span>
                                </div>

                                <div className="beans-picker-breakdown">
                                    {PROFILES.map((name) => (
                                        <span
                                            key={name}
                                            className={`beans-picker-badge${
                                                option.scores?.[name] === undefined ? ' beans-picker-badge-empty' : ''
                                            }`}
                                        >
                                            {name}: {option.scores?.[name] ?? '-'}
                                        </span>
                                    ))}
                                </div>

                                <div className="beans-picker-score-row">
                                    <span className="beans-picker-score-label">Your score:</span>
                                    {SCORE_VALUES.map((value) => (
                                        <button
                                            key={value}
                                            className={`beans-picker-score-button${
                                                myScore === value ? ' beans-picker-score-button-active' : ''
                                            }`}
                                            disabled={busyId === option.id}
                                            onClick={() => handleSetScore(option.id, value)}
                                        >
                                            {value}
                                        </button>
                                    ))}
                                    <button
                                        className="beans-picker-link-button beans-picker-remove-button"
                                        onClick={() => handleRemoveRestaurant(option.id)}
                                    >
                                        remove
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            <button className="beans-picker-clear-button" onClick={handleClearPoll}>
                Clear poll &amp; start over
            </button>
        </div>
    );
}

export default BeansPicker;